import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { getStoredRole, getTopNavLinks } from '../utils/navigation';

const C = { bg: '#0A0E1A', surface: '#111827', border: '#1F2937', accent: '#10B981', text: '#F9FAFB', muted: '#6B7280' };
const s = {
  page: { minHeight: '100vh', background: C.bg, color: C.text, fontFamily: "'DM Sans','Segoe UI',sans-serif" },
  topbar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 32px', borderBottom: `1px solid ${C.border}`, background: C.surface },
  logo: { fontSize: 20, fontWeight: 700, color: C.accent },
  nav: { display: 'flex', gap: 24, flexWrap: 'wrap' },
  navLink: (active) => ({ color: active ? C.accent : C.muted, textDecoration: 'none', fontSize: 14, fontWeight: active ? 700 : 400 }),
  main: { padding: 32, maxWidth: 1240, margin: '0 auto' },
  heading: { fontSize: 26, fontWeight: 700, marginBottom: 4 },
  sub: { fontSize: 14, color: C.muted, marginBottom: 24 },
  card: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '12px 16px', borderBottom: `1px solid ${C.border}` },
  td: { padding: '12px 16px', fontSize: 13, borderBottom: `1px solid ${C.border}22` },
  empty: { textAlign: 'center', padding: '48px 0', color: C.muted }
};

export default function Tenants() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', subdomain: '', status: 'active', plan_id: '' });
  const role = getStoredRole();
  const links = getTopNavLinks(role);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await api.get('/superadmin/tenants/data');
        setItems(Array.isArray(r.data) ? r.data : []);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const reload = async () => {
    const r = await api.get('/superadmin/tenants/data');
    setItems(Array.isArray(r.data) ? r.data : []);
  };

  const startEdit = (tenant) => {
    setEditingId(tenant.id);
    setForm({
      name: tenant.name || '',
      subdomain: tenant.subdomain || '',
      status: tenant.status || 'active',
      plan_id: tenant.plan_id ?? '',
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm({ name: '', subdomain: '', status: 'active', plan_id: '' });
  };

  const saveEdit = async (id) => {
    try {
      await api.put(`/superadmin/tenants/${id}`, {
        name: form.name,
        subdomain: form.subdomain,
        status: form.status,
        plan_id: form.plan_id === '' ? null : Number(form.plan_id),
      });
      await reload();
      cancelEdit();
    } catch (e) {
      alert(e?.response?.data?.error || 'No se pudo guardar el tenant');
    }
  };

  const toggleStatus = async (tenant) => {
    const nextStatus = tenant.status === 'suspended' ? 'active' : 'suspended';
    const label = nextStatus === 'active' ? 'activar' : 'desactivar';
    if (!window.confirm(`¿Deseas ${label} este tenant?`)) return;

    try {
      await api.put(`/superadmin/tenants/${tenant.id}`, {
        name: tenant.name,
        subdomain: tenant.subdomain,
        status: nextStatus,
        plan_id: tenant.plan_id,
      });
      await reload();
    } catch (e) {
      alert(e?.response?.data?.error || `No se pudo ${label} el tenant`);
    }
  };

  return (
    <div style={s.page}>
      <div style={s.topbar}>
        <div style={s.logo}>⚡ FintechCoop</div>
        <nav style={s.nav}>{links.map(link => <Link key={link.to} to={link.to} style={s.navLink(link.to === '/tenants')}>{link.label}</Link>)}</nav>
      </div>
      <div style={s.main}>
        <div style={s.heading}>Tenants</div>
        <div style={s.sub}>Administración multi-tenant de la plataforma</div>
        <div style={s.card}>
          {loading ? <div style={s.empty}>Cargando tenants...</div> : items.length === 0 ? <div style={s.empty}>Sin datos disponibles</div> : (
            <table style={s.table}>
              <thead><tr>{['ID', 'Nombre', 'Subdominio', 'Estado', 'Plan', 'Acciones'].map(h => <th key={h} style={s.th}>{h}</th>)}</tr></thead>
              <tbody>
                {items.map((t) => (
                  <tr key={t.id}>
                    <td style={s.td}>{t.id}</td>
                    <td style={s.td}>{editingId === t.id ? <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /> : (t.name || '—')}</td>
                    <td style={s.td}>{editingId === t.id ? <input value={form.subdomain} onChange={(e) => setForm({ ...form, subdomain: e.target.value })} /> : (t.subdomain || '—')}</td>
                    <td style={s.td}>{editingId === t.id ? (
                      <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                        <option value="active">active</option>
                        <option value="trial">trial</option>
                        <option value="suspended">suspended</option>
                      </select>
                    ) : (t.status || '—')}</td>
                    <td style={s.td}>{editingId === t.id ? <input value={form.plan_id} onChange={(e) => setForm({ ...form, plan_id: e.target.value })} /> : (t.plan_id || '—')}</td>
                    <td style={s.td}>
                      {editingId === t.id ? (
                        <>
                          <button onClick={() => saveEdit(t.id)} style={{ marginRight: 8 }}>Guardar</button>
                          <button onClick={cancelEdit}>Cancelar</button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => startEdit(t)} style={{ marginRight: 8 }}>Editar</button>
                          <button onClick={() => toggleStatus(t)}>{t.status === 'suspended' ? 'Activar' : 'Desactivar'}</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
